import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye,
  EyeOff,
  LogIn,
  UserPlus,
  CheckCircle,
  AlertCircle,
  Key,
  Mail,
  User,
  Phone,
  Building,
  Shield,
  Rocket,
  Network,
  Lock,
  CreditCard,
  Smartphone,
  Globe
} from 'lucide-react';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    company: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.email) {
      errors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email inválido';
    }

    if (!formData.password) {
      errors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!isLogin) {
      if (!formData.firstName) {
        errors.firstName = 'El nombre es requerido';
      }
      if (!formData.lastName) {
        errors.lastName = 'El apellido es requerido';
      }
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Las contraseñas no coinciden';
      }
    }

    return errors;
  }, [formData, isLogin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (error) setError('');
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // En el handleSubmit del Login
  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    setError('');
    setFormErrors({});

    try {
      let result;

      if (isLogin) {
        result = await login(formData.email, formData.password);
      } else {
        const registerData = {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          company: formData.company
        };
        result = await register(registerData);
      }

      if (result.success) {
        navigate('/admin/security');
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error de conexión. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setFormErrors({});
    setFormData(prev => ({
      ...prev,
      confirmPassword: ''
    }));
  };

  const isFormValid = Object.keys(validateForm()).length === 0;

  // Animaciones suaves
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const FormField = React.memo(({
    label,
    name,
    type,
    placeholder,
    icon: Icon,
    value,
    error,
    showValidation = false,
    ...props
  }) => (
    <motion.div variants={itemVariants} className="space-y-2">
      <label htmlFor={name} className="flex items-center text-sm font-medium text-gray-700">
        <Icon className="w-4 h-4 mr-2 text-blue-600" />
        {label}
      </label>
      <div className="relative">
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={handleChange}
          className={`w-full px-4 py-3 pl-10 bg-white border rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${error
              ? 'border-red-500 focus:ring-red-500'
              : 'border-gray-300 hover:border-blue-300'
            } ${showValidation && value && !error ? 'border-green-500 focus:ring-green-500' : ''}`}
          placeholder={placeholder}
          {...props}
        />
        <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        {showValidation && value && !error && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
          >
            <CheckCircle className="w-5 h-5 text-green-500" />
          </motion.div>
        )}
      </div>
      {error && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-red-500 text-sm mt-1 flex items-center"
        >
          <AlertCircle className="w-4 h-4 mr-1" />
          {error}
        </motion.p>
      )}
    </motion.div>
  ));

  const PasswordField = React.memo(({
    label,
    name,
    value,
    error,
    showPassword,
    onToggleVisibility,
    placeholder
  }) => (
    <motion.div variants={itemVariants} className="space-y-2">
      <label htmlFor={name} className="flex items-center text-sm font-medium text-gray-700">
        <Key className="w-4 h-4 mr-2 text-blue-600" />
        {label}
      </label>
      <div className="relative">
        <input
          id={name}
          name={name}
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={handleChange}
          className={`w-full px-4 py-3 pl-10 bg-white border rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-12 ${error
              ? 'border-red-500 focus:ring-red-500'
              : 'border-gray-300 hover:border-blue-300'
            }`}
          placeholder={placeholder}
        />
        <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <button
          type="button"
          onClick={onToggleVisibility}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-blue-600 transition-colors"
        >
          {showPassword ?
            <EyeOff className="w-5 h-5" /> :
            <Eye className="w-5 h-5" />
          }
        </button>
      </div>
      {error && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-red-500 text-sm mt-1 flex items-center"
        >
          <AlertCircle className="w-4 h-4 mr-1" />
          {error}
        </motion.p>
      )}
    </motion.div>
  ));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="w-full max-w-md"
      >
        {/* Card principal */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-8 text-center relative overflow-hidden">
            {/* Patrón de fondo */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-1/4 w-24 h-24 bg-white rounded-full"></div>
              <div className="absolute bottom-0 right-1/4 w-32 h-32 bg-white rounded-full"></div>
            </div>

            <motion.div
              whileHover={{ scale: 1.05, rotate: 3 }}
              whileTap={{ scale: 0.95 }}
              className="w-20 h-20 bg-white rounded-xl flex items-center justify-center mx-auto mb-6 shadow-xl relative z-10 p-2"
            >
              {/* Logo BBVA */}
              <div className="w-full h-full bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                <Lock className="w-8 h-8 text-white" />
              </div>
            </motion.div>

            <motion.h2
              className="text-3xl font-bold text-white mb-2 relative z-10"
              variants={itemVariants}
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
                APP BBVA
              </span>
            </motion.h2>
            <motion.p
              className="text-blue-100 font-medium relative z-10"
              variants={itemVariants}
            >
              {/* {isLogin ? 'Acceso Seguro' : 'Registro de Cliente'} */}
            </motion.p>
            <motion.p
              className="text-blue-200 text-sm mt-1 relative z-10"
              variants={itemVariants}
            >
              Plataforma de Préstamos Vehiculares
            </motion.p>
          </div>

          {/* Formulario */}
          <div className="p-8">
            <AnimatePresence mode="wait">
              <motion.form
                key={isLogin ? 'login' : 'register'}
                onSubmit={handleSubmit}
                className="space-y-6"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex items-center space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg"
                    >
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-red-700 text-sm font-medium">{error}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {!isLogin && (
                  <motion.div variants={itemVariants} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        label="Nombre"
                        name="firstName"
                        type="text"
                        placeholder="Juan"
                        icon={User}
                        value={formData.firstName}
                        error={formErrors.firstName}
                        showValidation={true}
                      />
                      <FormField
                        label="Apellido"
                        name="lastName"
                        type="text"
                        placeholder="Pérez"
                        icon={User}
                        value={formData.lastName}
                        error={formErrors.lastName}
                        showValidation={true}
                      />
                    </div>

                    <FormField
                      label="Teléfono"
                      name="phone"
                      type="tel"
                      placeholder="+51 987 654 321"
                      icon={Phone}
                      value={formData.phone}
                      error={formErrors.phone}
                    />

                    <FormField
                      label="Empresa"
                      name="company"
                      type="text"
                      placeholder="Mi Empresa S.A."
                      icon={Building}
                      value={formData.company}
                      error={formErrors.company}
                    />
                  </motion.div>
                )}

                <FormField
                  label="Correo Electrónico"
                  name="email"
                  type="email"
                  placeholder="cliente@bbva.com"
                  icon={Mail}
                  value={formData.email}
                  error={formErrors.email}
                  showValidation={true}
                />

                <PasswordField
                  label="Contraseña"
                  name="password"
                  value={formData.password}
                  error={formErrors.password}
                  showPassword={showPassword}
                  onToggleVisibility={() => setShowPassword(!showPassword)}
                  placeholder="Ingrese su contraseña"
                />

                {!isLogin && (
                  <PasswordField
                    label="Confirmar Contraseña"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    error={formErrors.confirmPassword}
                    showPassword={showConfirmPassword}
                    onToggleVisibility={() => setShowConfirmPassword(!showConfirmPassword)}
                    placeholder="Repita su contraseña"
                  />
                )}

                {/* Botón de Submit */}
                <motion.div variants={itemVariants}>
                  <motion.button
                    type="submit"
                    disabled={loading || !isFormValid}
                    whileHover={isFormValid ? { scale: 1.02, y: -2 } : {}}
                    whileTap={isFormValid ? { scale: 0.98 } : {}}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-4 rounded-lg font-semibold flex items-center justify-center space-x-3 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-xl"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>
                          {isLogin ? 'Accediendo...' : 'Registrando...'}
                        </span>
                      </>
                    ) : (
                      <>
                        {isLogin ?
                          <LogIn className="w-5 h-5" /> :
                          <UserPlus className="w-5 h-5" />
                        }
                        <span>
                          {isLogin ? 'Acceder al Sistema BBVA' : 'Crear Cuenta BBVA'}
                        </span>
                      </>
                    )}
                  </motion.button>
                </motion.div>

              </motion.form>
            </AnimatePresence>

            {/* Toggle entre Login y Registro */}
            <motion.div
              variants={itemVariants}
              className="mt-6 text-center"
            >
              <button
                onClick={toggleAuthMode}
                className="text-gray-600 hover:text-blue-600 transition-colors text-sm font-medium flex items-center justify-center mx-auto"
              >
                {isLogin ? (
                  <>
                    {/* <span className="text-blue-600 font-semibold hover:text-blue-700 flex items-center">
                      ¿Nuevo cliente? Solicite acceso
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </span> */}
                  </>
                ) : (
                  <>
                    <span className="text-blue-600 font-semibold hover:text-blue-700 flex items-center">
                      ¿Ya es cliente? Inicie sesión
                      <LogIn className="w-4 h-4 ml-1" />
                    </span>
                  </>
                )}
              </button>
            </motion.div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          variants={itemVariants}
          className="text-center mt-8"
        >
          <div className="text-gray-500 text-sm space-y-2">
            <p className="flex items-center justify-center">
              <Globe className="w-4 h-4 mr-2" />
              © 2025 Banco BBVA. Todos los derechos reservados.
            </p>
            <div className="flex items-center justify-center space-x-4 text-xs">
              <a href="#" className="text-gray-500 hover:text-blue-600">Términos y Condiciones</a>
              <span className="text-gray-400">•</span>
              <a href="#" className="text-gray-500 hover:text-blue-600">Privacidad</a>
              <span className="text-gray-400">•</span>
              <a href="#" className="text-gray-500 hover:text-blue-600">Seguridad</a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

// Agregamos el icono ArrowRight que falta
const ArrowRight = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
  </svg>
);

export default React.memo(Auth);